/**
 * DialogueEditor.jsx
 * Admin content editor for lesson_type === 'dialogue'
 *
 * Content shape:
 *   {
 *     scenario: string,
 *     dialogues: [
 *       { speaker, text, is_user, options: [{ text, is_correct, feedback }] }
 *     ]
 *   }
 *
 * Props:
 *   value    — current content object
 *   onChange — (newContent) => void
 */

import { useState } from 'react';
import { Plus, Trash2, ChevronUp, ChevronDown, ChevronRight, ChevronLeft } from 'lucide-react';
import { Input } from '../../ui/input';
import { Button } from '../../ui/button';
import { useLanguage } from '../../../contexts/LanguageContext';

const DEFAULT_OPTION = { text: '', is_correct: false, feedback: '' };
const DEFAULT_STEP = { speaker: '', text: '', is_user: false, options: [] };

export default function DialogueEditor({ value, onChange }) {
  const { t } = useLanguage();
  const scenario = value?.scenario || '';
  const dialogues = Array.isArray(value?.dialogues) ? value.dialogues : [];
  const [expandedStep, setExpandedStep] = useState(null);

  const update = (next) => onChange({ ...value, ...next });

  const addStep = () => {
    update({ dialogues: [...dialogues, { ...DEFAULT_STEP, options: [] }] });
    setExpandedStep(dialogues.length);
  };

  const removeStep = (idx) => {
    const next = dialogues.filter((_, i) => i !== idx);
    update({ dialogues: next });
    setExpandedStep(null);
  };

  const updateStep = (idx, field, val) =>
    update({
      dialogues: dialogues.map((s, i) => (i === idx ? { ...s, [field]: val } : s)),
    });

  const moveUp = (idx) => {
    if (idx === 0) return;
    const next = [...dialogues];
    [next[idx - 1], next[idx]] = [next[idx], next[idx - 1]];
    update({ dialogues: next });
    setExpandedStep(idx - 1);
  };

  const moveDown = (idx) => {
    if (idx === dialogues.length - 1) return;
    const next = [...dialogues];
    [next[idx], next[idx + 1]] = [next[idx + 1], next[idx]];
    update({ dialogues: next });
    setExpandedStep(idx + 1);
  };

  const addOption = (stepIdx) => {
    const step = dialogues[stepIdx];
    const opts = Array.isArray(step.options) ? step.options : [];
    updateStep(stepIdx, 'options', [...opts, { ...DEFAULT_OPTION }]);
  };

  const removeOption = (stepIdx, optIdx) => {
    const step = dialogues[stepIdx];
    const opts = (step.options || []).filter((_, i) => i !== optIdx);
    updateStep(stepIdx, 'options', opts);
  };

  const updateOption = (stepIdx, optIdx, field, val) => {
    const step = dialogues[stepIdx];
    const opts = (step.options || []).map((o, i) =>
      i === optIdx ? { ...o, [field]: val } : o,
    );
    updateStep(stepIdx, 'options', opts);
  };

  return (
    <div className="space-y-3">
      {/* Scenario */}
      <div>
        <label className="text-xs font-bold text-[var(--text-muted)] mb-1 block">
          {t('lessonEditor.dialogue.scenario')} *
        </label>
        <textarea
          rows={2}
          value={scenario}
          onChange={(e) => update({ scenario: e.target.value })}
          placeholder={t('lessonEditor.dialogue.scenarioPlaceholder')}
          className="w-full px-3 py-2 rounded-xl border-2 border-[var(--border-light)] bg-white text-sm font-semibold focus:border-[var(--brand-primary)] focus:outline-none resize-none"
        />
      </div>

      <div className="h-px bg-[var(--border-light)]" />

      {/* Steps */}
      {dialogues.length === 0 && (
        <p className="text-xs text-center text-[var(--text-muted)] py-4 border-2 border-dashed border-[var(--border-light)] rounded-xl">
          {t('lessonEditor.common.emptyList')}
        </p>
      )}
      {dialogues.map((step, idx) => (
        <div
          key={idx}
          className={`border-2 rounded-xl overflow-hidden transition-colors ${
            step.is_user
              ? 'border-cyan-300 bg-cyan-50'
              : 'border-[var(--border-light)] bg-[var(--bg-app)]'
          }`}
        >
          {/* Step header */}
          <div className="flex items-center gap-2 px-4 py-2">
            <span className="text-xs font-bold text-[var(--text-muted)] w-5">#{idx + 1}</span>
            <span
              className={`text-xs font-bold px-2 py-0.5 rounded-full ${
                step.is_user
                  ? 'bg-cyan-200 text-cyan-800'
                  : 'bg-gray-200 text-gray-700'
              }`}
            >
              {step.is_user ? t('lessonEditor.dialogue.isUserYes') : t('lessonEditor.dialogue.isUserNo')}
            </span>
            <span className="flex-1 text-xs font-semibold text-[var(--text-main)] truncate">
              {step.speaker ? `${step.speaker}: ` : ''}{step.text || '—'}
            </span>
            <div className="flex gap-1 ml-auto">
              <button
                type="button"
                onClick={() => moveUp(idx)}
                disabled={idx === 0}
                aria-label={t('lessonEditor.common.moveUp')}
                className="p-1 rounded disabled:opacity-30 text-[var(--text-muted)] hover:text-[var(--brand-primary)]"
              >
                <ChevronUp size={14} />
              </button>
              <button
                type="button"
                onClick={() => moveDown(idx)}
                disabled={idx === dialogues.length - 1}
                aria-label={t('lessonEditor.common.moveDown')}
                className="p-1 rounded disabled:opacity-30 text-[var(--text-muted)] hover:text-[var(--brand-primary)]"
              >
                <ChevronDown size={14} />
              </button>
              <button
                type="button"
                onClick={() => setExpandedStep(expandedStep === idx ? null : idx)}
                aria-label="toggle"
                className="p-1 rounded text-[var(--text-muted)] hover:text-[var(--brand-primary)]"
              >
                {expandedStep === idx ? <ChevronLeft size={14} /> : <ChevronRight size={14} />}
              </button>
              <button
                type="button"
                onClick={() => removeStep(idx)}
                aria-label={t('lessonEditor.dialogue.removeStep')}
                className="p-1 rounded hover:bg-red-100 text-[var(--text-muted)] hover:text-[var(--brand-danger)]"
              >
                <Trash2 size={14} />
              </button>
            </div>
          </div>

          {/* Step body — expanded */}
          {expandedStep === idx && (
            <div className="px-4 pb-4 space-y-3 border-t border-[var(--border-light)] pt-3">
              <div className="grid sm:grid-cols-2 gap-2">
                <div>
                  <label className="text-xs font-bold text-[var(--text-muted)] mb-1 block">
                    {t('lessonEditor.dialogue.speaker')}
                  </label>
                  <Input
                    value={step.speaker}
                    onChange={(e) => updateStep(idx, 'speaker', e.target.value)}
                    placeholder={t('lessonEditor.dialogue.speakerPlaceholder')}
                    className="rounded-xl border-2 border-[var(--border-light)] text-sm"
                  />
                </div>
                <div className="flex items-center gap-3 pt-5">
                  <label className="text-xs font-bold text-[var(--text-muted)]">
                    {t('lessonEditor.dialogue.isUser')}
                  </label>
                  <button
                    type="button"
                    onClick={() => updateStep(idx, 'is_user', !step.is_user)}
                    className={`relative inline-flex h-5 w-9 rounded-full transition-colors focus:outline-none border-2 ${
                      step.is_user
                        ? 'bg-cyan-500 border-cyan-600'
                        : 'bg-gray-200 border-gray-300'
                    }`}
                    role="switch"
                    aria-checked={step.is_user}
                  >
                    <span
                      className={`inline-block h-3.5 w-3.5 rounded-full bg-white shadow transform transition-transform mt-px ${
                        step.is_user ? 'translate-x-4' : 'translate-x-0.5'
                      }`}
                    />
                  </button>
                </div>
              </div>
              <div>
                <label className="text-xs font-bold text-[var(--text-muted)] mb-1 block">
                  {t('lessonEditor.dialogue.text')} {step.is_user ? '' : '*'}
                </label>
                <textarea
                  rows={2}
                  value={step.text}
                  onChange={(e) => updateStep(idx, 'text', e.target.value)}
                  placeholder={
                    step.is_user
                      ? t('lessonEditor.dialogue.textUserPlaceholder')
                      : t('lessonEditor.dialogue.textPlaceholder')
                  }
                  className="w-full px-3 py-2 rounded-xl border-2 border-[var(--border-light)] bg-white text-sm font-semibold focus:border-[var(--brand-primary)] focus:outline-none resize-none"
                />
              </div>

              {/* Options (only for user steps) */}
              {step.is_user && (
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <label className="text-xs font-bold text-[var(--text-muted)]">
                      {t('lessonEditor.dialogue.options')}
                    </label>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => addOption(idx)}
                      className="rounded-xl border-2 text-xs font-bold h-7 px-2"
                    >
                      <Plus size={12} className="mr-1" /> {t('lessonEditor.dialogue.addOption')}
                    </Button>
                  </div>
                  {(step.options || []).map((opt, oi) => (
                    <div
                      key={oi}
                      className={`p-3 rounded-xl border-2 space-y-1.5 ${
                        opt.is_correct
                          ? 'border-emerald-300 bg-emerald-50'
                          : 'border-[var(--border-light)] bg-white'
                      }`}
                    >
                      <div className="flex items-center gap-2">
                        <button
                          type="button"
                          onClick={() =>
                            updateOption(idx, oi, 'is_correct', !opt.is_correct)
                          }
                          className={`w-4 h-4 rounded border-2 flex items-center justify-center flex-shrink-0 ${
                            opt.is_correct
                              ? 'bg-emerald-500 border-emerald-600'
                              : 'border-gray-300'
                          }`}
                          role="checkbox"
                          aria-checked={opt.is_correct}
                          aria-label={t('lessonEditor.dialogue.isCorrect')}
                        >
                          {opt.is_correct && (
                            <svg viewBox="0 0 10 8" className="w-2.5 h-2 fill-white">
                              <path d="M1 4l3 3 5-6" strokeWidth="1.5" stroke="white" fill="none" />
                            </svg>
                          )}
                        </button>
                        <span className="text-xs font-bold text-[var(--text-muted)]">
                          {opt.is_correct ? t('lessonEditor.dialogue.isCorrectYes') : t('lessonEditor.dialogue.isCorrectNo')}
                        </span>
                        <button
                          type="button"
                          onClick={() => removeOption(idx, oi)}
                          aria-label={t('lessonEditor.dialogue.removeOption')}
                          className="ml-auto p-0.5 rounded hover:bg-red-100 text-[var(--text-muted)] hover:text-[var(--brand-danger)]"
                        >
                          <Trash2 size={12} />
                        </button>
                      </div>
                      <Input
                        value={opt.text}
                        onChange={(e) => updateOption(idx, oi, 'text', e.target.value)}
                        placeholder={t('lessonEditor.dialogue.optionTextPlaceholder')}
                        className="rounded-xl border-2 border-[var(--border-light)] text-xs"
                      />
                      <Input
                        value={opt.feedback}
                        onChange={(e) => updateOption(idx, oi, 'feedback', e.target.value)}
                        placeholder={t('lessonEditor.dialogue.feedbackPlaceholder')}
                        className="rounded-xl border-2 border-[var(--border-light)] text-xs"
                      />
                    </div>
                  ))}
                  {(step.options || []).length === 0 && (
                    <p className="text-xs text-[var(--text-muted)] italic">
                      {t('lessonEditor.dialogue.noOptions')}
                    </p>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      ))}

      <Button
        type="button"
        variant="outline"
        size="sm"
        onClick={addStep}
        className="rounded-xl border-2 border-dashed border-[var(--border-heavy)] text-[var(--text-muted)] hover:border-[var(--brand-primary)] hover:text-[var(--brand-primary)] font-bold w-full"
      >
        <Plus size={14} className="mr-1" /> {t('lessonEditor.dialogue.addStep')}
      </Button>
    </div>
  );
}
